import { QueryClient } from '@tanstack/react-query'
import {
  transactionsKeys,
  accountsKeys,
  purchasesKeys,
  customersKeys,
  productsKeys,
  usageKeys,
  plansKeys,
  adminTransactionsKeys,
  adminProductsKeys,
  notificationsKeys,
} from 'hooks/queries/keys'
import type { InvalidationRule } from './types'
import type { Transaction, TransactionsResponse } from 'types/transaction'
import type { Customer } from 'types/customers'
// ---------------------------------------------------------------------------
// Optimistic updater helpers
// ---------------------------------------------------------------------------

function patchTransactionInCache(queryClient: QueryClient, updated: Transaction) {
  queryClient.setQueriesData<TransactionsResponse>(
    { queryKey: transactionsKeys.all() },
    existing => {
      if (!existing?.transactions) return existing
      const idx = existing.transactions.findIndex(t => t.transactionId === updated.transactionId)
      if (idx === -1) return existing
      const transactions = [...existing.transactions]
      transactions[idx] = { ...transactions[idx], ...updated }
      return { ...existing, transactions }
    },
  )
}

function appendTransactionToCache(queryClient: QueryClient, tx: Transaction) {
  queryClient.setQueriesData<TransactionsResponse>(
    { queryKey: transactionsKeys.all() },
    existing => {
      if (!existing?.transactions) return existing
      const already = existing.transactions.some(t => t.transactionId === tx.transactionId)
      if (already) return existing
      return {
        ...existing,
        transactions: [tx, ...existing.transactions],
        total: (existing.total ?? 0) + 1,
      }
    },
  )
}

function appendCustomerToCache(queryClient: QueryClient, customer: Customer) {
  queryClient.setQueriesData<Customer[]>({ queryKey: customersKeys.all() }, existing => {
    if (!Array.isArray(existing)) return existing
    if (existing.some(c => c.id === customer.id)) return existing
    return [...existing, customer]
  })
}

function patchCustomerInCache(queryClient: QueryClient, customer: Customer) {
  queryClient.setQueryData(customersKeys.detail(customer.id), customer)
  queryClient.setQueriesData<Customer[]>({ queryKey: customersKeys.all() }, existing => {
    if (!Array.isArray(existing)) return existing
    return existing.map(c => (c.id === customer.id ? customer : c))
  })
}

function removeCustomerFromCache(queryClient: QueryClient, customerId: string) {
  queryClient.setQueriesData<Customer[]>({ queryKey: customersKeys.all() }, existing => {
    if (!Array.isArray(existing)) return existing
    return existing.filter(c => c.id !== customerId)
  })
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

export const rules: InvalidationRule[] = [
  // ── Transactions ────────────────────────────────────────────────────────
  {
    event: 'transaction:created',
    invalidate: [
      transactionsKeys.all(),
      accountsKeys.all(),
      purchasesKeys.all(),
      usageKeys.all(),
      ['providerHome'],
      adminTransactionsKeys.all(),
    ],
    debounceMs: 300,
    updater: (qc, payload) => {
      try {
        const tx = payload as Transaction
        if (tx?.transactionId) appendTransactionToCache(qc, tx)
      } catch {
        /* fall through to invalidation */
      }
    },
  },
  {
    event: 'transaction:update',
    invalidate: [transactionsKeys.all(), adminTransactionsKeys.all()],
    updater: (qc, payload) => {
      try {
        const tx = payload as Transaction
        if (tx?.transactionId) patchTransactionInCache(qc, tx)
      } catch {
        /* fall through to invalidation */
      }
    },
  },
  {
    event: 'transaction:status-changed',
    invalidate: [transactionsKeys.all(), purchasesKeys.all(), adminTransactionsKeys.all()],
    updater: (qc, payload) => {
      try {
        const data = payload as { transactionId: string; status: string }
        if (!data?.transactionId) return
        qc.setQueriesData<TransactionsResponse>({ queryKey: transactionsKeys.all() }, existing => {
          if (!existing?.transactions) return existing
          const idx = existing.transactions.findIndex(t => t.transactionId === data.transactionId)
          if (idx === -1) return existing
          const transactions = [...existing.transactions]
          transactions[idx] = { ...transactions[idx], status: data.status as any }
          return { ...existing, transactions }
        })
      } catch {
        /* fall through to invalidation */
      }
    },
  },

  // ── Customers ───────────────────────────────────────────────────────────
  {
    event: 'customer:created',
    invalidate: [customersKeys.all(), customersKeys.stats(), ['providerHome']],
    updater: (qc, payload) => {
      try {
        const data = payload as { customer: Customer }
        if (data?.customer) appendCustomerToCache(qc, data.customer)
      } catch {
        /* fall through to invalidation */
      }
    },
  },
  {
    event: 'customer:updated',
    invalidate: [customersKeys.all(), customersKeys.stats()],
    updater: (qc, payload) => {
      try {
        const data = payload as { customer: Customer }
        if (data?.customer) patchCustomerInCache(qc, data.customer)
      } catch {
        /* fall through to invalidation */
      }
    },
  },
  {
    event: 'customer:deleted',
    invalidate: [customersKeys.all(), customersKeys.stats()],
    updater: (qc, payload) => {
      try {
        const data = payload as { customerId: string }
        if (data?.customerId) removeCustomerFromCache(qc, data.customerId)
      } catch {
        /* fall through to invalidation */
      }
    },
  },

  // ── Products ────────────────────────────────────────────────────────────
  {
    event: 'product:created',
    invalidate: [
      productsKeys.all(),
      productsKeys.stats(),
      ['providerHome'],
      adminProductsKeys.all(),
    ],
  },
  {
    event: 'product:updated',
    invalidate: [productsKeys.all(), productsKeys.stats(), adminProductsKeys.all()],
  },
  {
    event: 'product:deleted',
    invalidate: [productsKeys.all(), productsKeys.stats(), adminProductsKeys.all()],
  },

  // ── Notifications ───────────────────────────────────────────────────────
  {
    event: 'notification:new',
    invalidate: [
      notificationsKeys.all(),
      notificationsKeys.stats(),
      notificationsKeys.unreadCount(),
    ],
  },
  {
    event: 'notification:count-update',
    invalidate: [notificationsKeys.unreadCount()],
  },

  // ── Plans ──────────────────────────────────────────────────────────────
  {
    event: 'plan:created',
    invalidate: [plansKeys.all(), ['providerHome']],
  },
  {
    event: 'plan:updated',
    invalidate: [plansKeys.all()],
  },
  {
    event: 'plan:deleted',
    invalidate: [plansKeys.all()],
  },

  // ── Purchases ─────────────────────────────────────────────────────────
  {
    event: 'purchase:created',
    invalidate: [purchasesKeys.all(), customersKeys.all(), ['providerHome']],
  },
  {
    event: 'purchase:updated',
    invalidate: [purchasesKeys.all()],
  },
  {
    event: 'purchase:deleted',
    invalidate: [purchasesKeys.all(), customersKeys.all()],
  },
]
